import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Card from '../components/Card';
import Badge from '../components/Badge';
import Button from '../components/Button';
import Modal from '../components/Modal';
import { typeGroupColor, workStyleLabel, getTypeGroup, GROUP_LABELS, TYPE_NAMES } from '../lib/constants';
import { api } from '../lib/api';
import { useAI } from '../hooks/useAI';
import type { Employee, ModeAResponse, Department, PersonalityType } from '../lib/types';

export default function EmployeeDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { recommendWorkstyle, loading: aiLoading, error: aiError } = useAI();

  const [employee, setEmployee] = useState<Employee | null>(null);
  const [loading, setLoading] = useState(true);
  const [aiResult, setAiResult] = useState<ModeAResponse | null>(null);
  const [aiCachedAt, setAiCachedAt] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [editForm, setEditForm] = useState({
    name: '', employee_code: '', department_id: '', position: '',
    job_description: '', personality_type: '', work_style: '', consent_given: false,
  });

  const loadEmployee = useCallback(async () => {
    if (!id) return;
    try {
      const { employee: emp } = await api.employees.get(id);
      const e = emp as unknown as Employee;
      setEmployee(e);
      setEditForm({
        name: e.name,
        employee_code: e.employee_code || '',
        department_id: e.department_id || '',
        position: e.position || '',
        job_description: e.job_description || '',
        personality_type: e.personality_type || '',
        work_style: e.work_style || '',
        consent_given: !!e.consent_given,
      });
      if (e.ai_recommendation) {
        try {
          const cached = JSON.parse(e.ai_recommendation) as ModeAResponse;
          setAiResult(cached);
          setAiCachedAt(e.ai_analyzed_at);
        } catch { /* ignore */ }
      }
    } catch {
      navigate('/employees');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { loadEmployee(); }, [loadEmployee]);

  useEffect(() => {
    api.departments.list().then(res => {
      setDepartments(res.departments as unknown as Department[]);
    }).catch(() => {});
  }, []);

  const handleAIRecommend = async (forceRefresh = false) => {
    if (!id) return;
    const result = await recommendWorkstyle(id, forceRefresh);
    if (result) {
      setAiResult(result);
      setAiCachedAt(new Date().toISOString());
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    try {
      const data: Record<string, unknown> = {
        name: editForm.name,
        consent_given: editForm.consent_given,
      };
      if (editForm.employee_code) data.employee_code = editForm.employee_code;
      if (editForm.department_id) data.department_id = editForm.department_id;
      if (editForm.position) data.position = editForm.position;
      data.job_description = editForm.job_description || '';
      if (editForm.personality_type) data.personality_type = editForm.personality_type;
      if (editForm.work_style) data.work_style = editForm.work_style;

      await api.employees.update(id, data);
      setShowEditModal(false);
      // Reset AI cache if type or job_description changed
      if (editForm.personality_type !== (employee?.personality_type || '')
        || editForm.job_description !== (employee?.job_description || '')) {
        setAiResult(null);
        setAiCachedAt(null);
      }
      loadEmployee();
    } catch { /* handle silently */ }
  };

  if (loading || !employee) {
    return <div className="flex items-center justify-center h-64 text-text-muted text-sm">読み込み中...</div>;
  }

  const ws = workStyleLabel(employee.work_style);
  const tc = typeGroupColor(employee.personality_type);
  const group = getTypeGroup(employee.personality_type);
  const groupLabel = group ? GROUP_LABELS[group] : '';
  const typeName = employee.personality_type ? TYPE_NAMES[employee.personality_type as PersonalityType] || '' : '';

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return `${d.getFullYear()}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getDate().toString().padStart(2, '0')} ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
  };

  // Get initials for avatar (first char of name)
  const initial = employee.name.charAt(0);

  return (
    <div>
      {/* Breadcrumb + Edit button */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2 text-[13px]">
          <span
            onClick={() => navigate('/employees')}
            className="text-text-sub cursor-pointer hover:text-primary transition-colors"
          >
            社員一覧
          </span>
          <span className="text-text-muted">&gt;</span>
          <span className="text-text-main font-medium">{employee.name}</span>
        </div>
        <button
          onClick={() => setShowEditModal(true)}
          className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-border bg-surface hover:bg-surface-hover text-text-main text-[13px] transition-colors"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
          </svg>
          編集
        </button>
      </div>

      {/* ===== ROW 1: Profile + AI Recommend + Risk Signs ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">

        {/* LEFT: Profile Card */}
        <Card className="lg:row-span-2">
          {/* Avatar + Name */}
          <div className="flex flex-col items-center text-center pb-5 border-b border-border mb-5 sm:flex-row sm:text-left sm:items-start sm:gap-5 lg:flex-col lg:text-center lg:items-center">
            <div
              className="w-24 h-24 rounded-full flex items-center justify-center text-3xl font-bold mb-3 sm:mb-0 lg:mb-3 shrink-0"
              style={{ background: `${tc}20`, color: tc, border: `3px solid ${tc}40` }}
            >
              {initial}
            </div>
            <div>
              <h2 className="text-xl font-bold text-text-main mb-1">{employee.name}</h2>
              <p className="text-[13px] text-text-sub mb-3">
                {employee.position || '-'} · {employee.department_name || '-'}
              </p>
              <div className="flex items-center justify-center gap-2 flex-wrap sm:justify-start lg:justify-center">
                <Badge
                  bg={`${tc}22`}
                  color={tc}
                  className="!text-[14px] !font-bold !px-4 !py-1.5"
                >
                  {employee.personality_type || '?'}
                </Badge>
              </div>
              {(employee.personality_type && typeName) && (
                <p className="text-[11px] text-text-muted mt-1.5">
                  {typeName} ({groupLabel})
                </p>
              )}
              <div className="flex items-center justify-center gap-2 mt-3 flex-wrap sm:justify-start lg:justify-center">
                <Badge className={ws.colorClass}>{ws.label}</Badge>
                {employee.consent_given ? (
                  <Badge bg="rgba(74,222,128,0.1)" color="#4ADE80">同意済み</Badge>
                ) : (
                  <Badge bg="rgba(255,107,107,0.1)" color="#FF6B6B">同意未取得</Badge>
                )}
              </div>
            </div>
          </div>

          {/* Basic Info */}
          <div className="space-y-4">
            {employee.employee_code && (
              <div>
                <div className="text-[11px] text-text-muted mb-0.5">社員番号</div>
                <div className="text-[13px] text-text-main">{employee.employee_code}</div>
              </div>
            )}
            <div>
              <div className="text-[11px] text-text-muted mb-0.5">部署</div>
              <div className="text-[13px] text-text-main">{employee.department_name || '-'}</div>
            </div>
            <div>
              <div className="text-[11px] text-text-muted mb-0.5">役職</div>
              <div className="text-[13px] text-text-main">{employee.position || '-'}</div>
            </div>
            <div>
              <div className="text-[11px] text-text-muted mb-0.5">登録日</div>
              <div className="text-[13px] text-text-main">{formatDate(employee.created_at)}</div>
            </div>
          </div>
        </Card>

        {/* CENTER-TOP: AI Recommendation */}
        <Card className="!border-border-active">
          <div className="flex items-center gap-2 mb-4">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4A7CFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
            <span className="text-sm font-semibold text-primary">AI 働き方レコメンド</span>
            <Badge bg="rgba(74,124,255,0.1)" color="#4A7CFF">Claude AI</Badge>
          </div>

          {!aiResult ? (
            <div className="text-center py-6">
              <Button
                primary
                small
                onClick={() => handleAIRecommend(false)}
                disabled={aiLoading || !employee.consent_given || !employee.personality_type}
              >
                {aiLoading ? '分析中...' : 'AIレコメンドを生成する'}
              </Button>
              {!employee.consent_given && (
                <p className="text-[11px] text-accent mt-2">AI分析には本人の同意が必要です</p>
              )}
              {!employee.personality_type && (
                <p className="text-[11px] text-accent mt-2">16タイプを入力してください</p>
              )}
              <p className="text-[11px] text-text-muted mt-2">
                ※ タイプと役職のみAIに送信。氏名は送信されません。
              </p>
              {aiError && <p className="text-[11px] text-accent mt-2">{aiError}</p>}
            </div>
          ) : (
            <div>
              {/* 推奨ワークスタイル */}
              <div className="mb-3">
                <span className="text-[13px] text-text-sub">推奨：</span>
                {(() => {
                  const rws = workStyleLabel(aiResult.recommended_work_style);
                  return (
                    <span className={`inline-block ml-1 text-sm font-bold px-3 py-1 rounded-md ${rws.colorClass}`}>
                      {rws.label}
                    </span>
                  );
                })()}
              </div>

              {/* Summary */}
              <p className="text-[13px] text-text-sub leading-[1.8] mb-4">
                {aiResult.summary}
              </p>

              {/* Cache info + re-analyze */}
              <div className="flex items-center justify-between pt-3 border-t border-border">
                <div className="flex items-center gap-1.5">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#4ADE80" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                  <span className="text-[11px] text-text-muted">
                    分析済み {aiCachedAt ? formatDate(aiCachedAt) : ''}
                  </span>
                </div>
                <button
                  onClick={() => handleAIRecommend(true)}
                  disabled={aiLoading}
                  className="flex items-center gap-1 px-2.5 py-1 rounded-md text-[12px] font-medium text-primary border border-primary/30 hover:bg-primary/10 transition-colors disabled:opacity-50"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                  </svg>
                  {aiLoading ? '再分析中...' : '再分析する'}
                </button>
              </div>

              <p className="text-[10px] text-text-muted mt-2 leading-relaxed">
                {aiResult.disclaimer || '※ 16タイプ分析に基づく参考情報です。人事判断の唯一の基準としてではなく、他の情報と組み合わせてご活用ください。'}
              </p>
            </div>
          )}
        </Card>

        {/* RIGHT-TOP: Risk Signs */}
        {aiResult ? (
          <Card>
            <div className="flex items-center gap-2 mb-4">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#FF6B6B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              <span className="text-sm font-semibold text-accent">注意すべきサイン</span>
            </div>
            <ul className="space-y-3">
              {aiResult.risk_signs.signs.map((s, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <span className="text-accent mt-1 shrink-0">&#x2022;</span>
                  <span className="text-[13px] text-text-sub leading-[1.7]">{s}</span>
                </li>
              ))}
            </ul>
          </Card>
        ) : (
          <Card className="flex items-center justify-center">
            <p className="text-[13px] text-text-muted py-8">AI分析を実行すると結果が表示されます</p>
          </Card>
        )}

        {/* ===== ROW 2: (Profile continues via row-span) + Strengths + Management Tips ===== */}

        {/* CENTER-BOTTOM: Strengths */}
        {aiResult ? (
          <Card>
            <div className="flex items-center gap-2 mb-4">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4ADE80" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
              </svg>
              <span className="text-sm font-semibold text-success">強み</span>
            </div>
            <div className="space-y-4">
              {aiResult.strengths.map((s, i) => (
                <div key={i} className={`${i < aiResult.strengths.length - 1 ? 'pb-3 border-b border-border' : ''}`}>
                  <p className="text-[13px] text-text-sub leading-[1.7]">{s}</p>
                </div>
              ))}
            </div>
          </Card>
        ) : (
          <Card className="flex items-center justify-center">
            <p className="text-[13px] text-text-muted py-8">AI分析を実行すると結果が表示されます</p>
          </Card>
        )}

        {/* RIGHT-BOTTOM: Management Tips */}
        {aiResult ? (
          <Card>
            <div className="flex items-center gap-2 mb-4">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4A7CFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              <span className="text-sm font-semibold text-primary">マネジメントTips</span>
            </div>
            <ul className="space-y-3">
              {aiResult.management_tips.map((s, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <span className="text-primary mt-1 shrink-0">&#x2022;</span>
                  <span className="text-[13px] text-text-sub leading-[1.7]">{s}</span>
                </li>
              ))}
            </ul>
          </Card>
        ) : (
          <Card className="flex items-center justify-center">
            <p className="text-[13px] text-text-muted py-8">AI分析を実行すると結果が表示されます</p>
          </Card>
        )}
      </div>

      {/* Re-analysis recommended banner (old cache without job_fitness) */}
      {aiResult && !aiResult.job_fitness && (
        <div className="mb-4">
          <div className="flex items-center justify-between p-4 rounded-xl border border-primary/30 bg-primary/5">
            <div className="flex items-center gap-3">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#4A7CFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <div>
                <p className="text-[13px] font-medium text-text-main">適合度スコアを取得するには再分析が必要です</p>
                <p className="text-[11px] text-text-sub mt-0.5">「再分析する」で最新のAI分析結果を取得できます（業務内容が設定済みの場合、詳細な適合度評価が表示されます）</p>
              </div>
            </div>
            <button
              onClick={() => handleAIRecommend(true)}
              disabled={aiLoading}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-[13px] font-medium text-white bg-primary hover:bg-primary-hover transition-colors disabled:opacity-50 shrink-0 ml-4"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
              </svg>
              {aiLoading ? '分析中...' : '再分析する'}
            </button>
          </div>
        </div>
      )}

      {/* ===== Job Fitness Section ===== */}
      {aiResult?.job_fitness && (() => {
        const jf = aiResult.job_fitness;
        const scoreColors: Record<number, { bg: string; text: string }> = {
          5: { bg: 'rgba(74,222,128,0.15)', text: '#4ADE80' },
          4: { bg: 'rgba(74,124,255,0.15)', text: '#4A7CFF' },
          3: { bg: 'rgba(250,204,21,0.15)', text: '#FACC15' },
          2: { bg: 'rgba(255,159,67,0.15)', text: '#FF9F43' },
          1: { bg: 'rgba(255,107,107,0.15)', text: '#FF6B6B' },
          0: { bg: 'rgba(128,128,128,0.15)', text: '#888888' },
        };
        const sc = scoreColors[jf.score] || scoreColors[0];
        const burnoutColors: Record<string, { bg: string; text: string; label: string }> = {
          low: { bg: 'rgba(74,222,128,0.12)', text: '#4ADE80', label: '低' },
          medium: { bg: 'rgba(250,204,21,0.12)', text: '#FACC15', label: '中' },
          high: { bg: 'rgba(255,107,107,0.12)', text: '#FF6B6B', label: '高' },
        };
        const bc = burnoutColors[jf.burnout_risk.level] || burnoutColors.medium;

        return (
          <div className="mb-4">
            <Card>
              {/* Header */}
              <div className="flex items-center gap-2 mb-5">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4A7CFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="8.5" cy="7" r="4" /><polyline points="17 11 19 13 23 9" />
                </svg>
                <span className="text-sm font-semibold text-primary">業務適合度スコア</span>
              </div>

              {/* Score + Label + Summary */}
              <div className={`flex items-start gap-4 ${jf.score > 0 ? 'mb-5 pb-5 border-b border-border' : 'mb-3'}`}>
                <div
                  className="w-14 h-14 rounded-xl flex items-center justify-center text-2xl font-bold shrink-0"
                  style={{ background: sc.bg, color: sc.text, border: `2px solid ${sc.text}30` }}
                >
                  {jf.score}
                </div>
                <div>
                  <p className="text-[15px] font-semibold text-text-main mb-1">{jf.label}</p>
                  <p className="text-[13px] text-text-sub leading-[1.7]">{jf.summary}</p>
                </div>
              </div>

              {jf.score === 0 && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-warning/5 border border-warning/20">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#FBBF24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  <div>
                    <p className="text-[13px] text-text-main">業務内容を入力すると、詳細な適合度評価が表示されます</p>
                    <p className="text-[11px] text-text-sub mt-0.5">編集ボタン → 「業務内容」欄に業務の概要を入力 → 保存後に再分析してください</p>
                  </div>
                </div>
              )}

              {jf.score > 0 && (<>
              {/* Matching Points + Friction Points */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5 pb-5 border-b border-border">
                {/* Matching Points */}
                <div>
                  <div className="flex items-center gap-1.5 mb-3">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4ADE80" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    <span className="text-[13px] font-semibold text-success">適合ポイント</span>
                  </div>
                  <ul className="space-y-2">
                    {jf.matching_points.map((p, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="text-success mt-0.5 shrink-0 text-[12px]">&#x2713;</span>
                        <span className="text-[13px] text-text-sub leading-[1.7]">{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Friction Points */}
                <div>
                  <div className="flex items-center gap-1.5 mb-3">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#FF9F43" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                    <span className="text-[13px] font-semibold" style={{ color: '#FF9F43' }}>摩擦ポイント</span>
                  </div>
                  <ul className="space-y-2">
                    {jf.friction_points.map((p, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span style={{ color: '#FF9F43' }} className="mt-0.5 shrink-0 text-[12px]">&#x26A0;</span>
                        <span className="text-[13px] text-text-sub leading-[1.7]">{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Burnout Risk */}
              <div className="mb-5 pb-5 border-b border-border">
                <div className="flex items-center gap-2 mb-3">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#FF6B6B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z" />
                  </svg>
                  <span className="text-[13px] font-semibold text-accent">バーンアウトリスク</span>
                  <span
                    className="text-[11px] font-bold px-2 py-0.5 rounded-md"
                    style={{ background: bc.bg, color: bc.text }}
                  >
                    {bc.label}
                  </span>
                </div>
                <div className="space-y-2 pl-5">
                  <div>
                    <span className="text-[11px] text-text-muted">トリガー：</span>
                    <span className="text-[13px] text-text-sub">{jf.burnout_risk.trigger}</span>
                  </div>
                  <div>
                    <span className="text-[11px] text-text-muted">予防策：</span>
                    <span className="text-[13px] text-text-sub">{jf.burnout_risk.prevention}</span>
                  </div>
                </div>
              </div>

              {/* Alternative Roles */}
              {jf.alternative_roles.length > 0 && (
                <div>
                  <div className="flex items-center gap-1.5 mb-3">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4A7CFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="16 3 21 3 21 8" /><line x1="4" y1="20" x2="21" y2="3" /><polyline points="21 16 21 21 16 21" /><line x1="15" y1="15" x2="21" y2="21" /><line x1="4" y1="4" x2="9" y2="9" />
                    </svg>
                    <span className="text-[13px] font-semibold text-primary">適性のある他の役割</span>
                  </div>
                  <div className="space-y-2">
                    {jf.alternative_roles.map((ar, i) => (
                      <div key={i} className="flex items-start gap-2 pl-5">
                        <span className="text-primary mt-0.5 shrink-0 text-[12px]">&#x2022;</span>
                        <div>
                          <span className="text-[13px] font-medium text-text-main">{ar.role}</span>
                          <span className="text-[13px] text-text-sub ml-1.5">- {ar.reason}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              </>)}
            </Card>
          </div>
        );
      })()}

      {/* Edit Modal */}
      <Modal open={showEditModal} onClose={() => setShowEditModal(false)} title="社員情報を編集">
        <form onSubmit={handleUpdate} className="space-y-3">
          <div>
            <label className="block text-xs text-text-sub mb-1">氏名 *</label>
            <input
              required
              value={editForm.name}
              onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg border border-border bg-surface text-text-main text-sm outline-none focus:border-primary"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-text-sub mb-1">社員番号</label>
              <input
                value={editForm.employee_code}
                onChange={e => setEditForm(p => ({ ...p, employee_code: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border border-border bg-surface text-text-main text-sm outline-none focus:border-primary"
              />
            </div>
            <div>
              <label className="block text-xs text-text-sub mb-1">部署</label>
              <select
                value={editForm.department_id}
                onChange={e => setEditForm(p => ({ ...p, department_id: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border border-border bg-surface text-text-main text-sm outline-none focus:border-primary"
              >
                <option value="">選択なし</option>
                {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-text-sub mb-1">16タイプ</label>
              <select
                value={editForm.personality_type}
                onChange={e => setEditForm(p => ({ ...p, personality_type: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border border-border bg-surface text-text-main text-sm outline-none focus:border-primary"
              >
                <option value="">未入力</option>
                {['ENTJ','ENTP','ENFJ','ENFP','ESTJ','ESTP','ESFJ','ESFP','INTJ','INTP','INFJ','INFP','ISTJ','ISTP','ISFJ','ISFP'].map(t =>
                  <option key={t} value={t}>{t}</option>
                )}
              </select>
            </div>
            <div>
              <label className="block text-xs text-text-sub mb-1">役職</label>
              <input
                value={editForm.position}
                onChange={e => setEditForm(p => ({ ...p, position: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border border-border bg-surface text-text-main text-sm outline-none focus:border-primary"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs text-text-sub mb-1">業務内容</label>
            <textarea
              value={editForm.job_description}
              onChange={e => setEditForm(p => ({ ...p, job_description: e.target.value }))}
              placeholder="例：営業企画、チームリーダー、データ分析、顧客対応など"
              rows={3}
              className="w-full px-3 py-2 rounded-lg border border-border bg-surface text-text-main text-sm outline-none focus:border-primary resize-none"
            />
            <p className="text-[10px] text-text-muted mt-0.5">※ AI適合度分析・配置転換レコメンド・燃え尽きリスク判定に使用されます</p>
          </div>
          <div>
            <label className="block text-xs text-text-sub mb-1">働き方</label>
            <select
              value={editForm.work_style}
              onChange={e => setEditForm(p => ({ ...p, work_style: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg border border-border bg-surface text-text-main text-sm outline-none focus:border-primary"
            >
              <option value="">未設定</option>
              <option value="remote">リモート</option>
              <option value="office">出社</option>
              <option value="hybrid">ハイブリッド</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={editForm.consent_given}
              onChange={e => setEditForm(p => ({ ...p, consent_given: e.target.checked }))}
              className="rounded"
            />
            <label className="text-xs text-text-sub">AI分析への同意済み</label>
          </div>
          {editForm.personality_type !== (employee.personality_type || '') && aiResult && (
            <div className="text-[11px] text-warning bg-warning/10 px-3 py-2 rounded-lg">
              タイプを変更するとAI分析結果がリセットされます
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button onClick={() => setShowEditModal(false)}>キャンセル</Button>
            <Button primary type="submit">更新</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
